import { ScreenPoint, PointerButton, CanvasEngine, DrawContext } from '@cs/engine';
import { World } from './world';
import { JsonDictionaryWrapper } from './utils';
import { UI } from './ui';
import { TickAction, LocalPacketIO, WebSocketIO, Command, Packet, PacketBuffer, PacketIO } from './command';
import { Renderer, Viewport, ViewportTransformation } from './renderer';

function extract_multiplayer_id(path: string): string | null {
    const cpts = path.split("/")
    if (cpts.length < 3) {
        return null
    }

    if (cpts[1] != "multiplayer") {
        return null
    }

    return cpts[2]
}

export class Game extends CanvasEngine {
    private world: World
    private viewport: Viewport
    private pannable_viewport: ViewportTransformation
    private renderer!: Renderer
    private ui: UI
    private dragged_screen_point: ScreenPoint | null = null
    private packets: PacketBuffer
    private io: PacketIO

    constructor(canvas_name: string) {
        super(canvas_name)

        this.world = new World()

        const mpid = extract_multiplayer_id(window.location.pathname)
        if (mpid != null) {
            this.io = new WebSocketIO(
                mpid,
                () => JSON.stringify(this.world),
                // TODO load_json to take string
                (ns: string) => this.world.load_json(JsonDictionaryWrapper.create_from_string(ns)),
                this.world
            )
        } else {
            this.io = new LocalPacketIO()
        }

        this.packets = new PacketBuffer(this.io)
        this.viewport = new Viewport()
        this.pannable_viewport = new ViewportTransformation()
        this.ui = new UI(this.world, (cmd: Command) => { this.commit_command(cmd); })
    }

    protected async handle_initialise(): Promise<void> {
        this.set_background("rgb(106, 187, 147)")
        this.set_effective_width(40)
        let atlas = await this.load_atlas("./landscape.png", 100)
        this.renderer = new Renderer(atlas, this.viewport)
        this.intercept_pointer_events()
        this.start_ticking()

        this.pannable_viewport.snap_to(this.width() * 0.5, this.height() * 0.5)
    }

    protected handle_tick(): void {
        if (this.ui.paused) {
            // still need to redisplay drags, etc
            this.set_needs_display()
            return
        }

        const [cmds, action] = this.packets.tick()
        if (cmds != null) {
            for (let cmd of cmds) {
                this.world.execute_command(cmd)
            }
        }

        if (action == TickAction.Tick) {
            const should_update = this.world.tick()
            this.set_needs_display()

            const tuw = this.world.director.ticks_until_wave
            if (tuw % 60 == 0) {
                this.ui.set_wave_status(this.world.director.wave_index, tuw)
            }
        }
    }

    // called when this wants to run a command for sure
    private commit_command(cmd: Command): void {
        this.packets.add_command(cmd)
    }

    protected handle_render(ctx: DrawContext): void {
        this.pannable_viewport.state.transform_ctx(ctx)

       const tl = this.viewport.screen_to_world_vertex(this.pannable_viewport.state.pannable_screen_to_virtual_screen(new ScreenPoint(0, 0)))
       const br = this.viewport.screen_to_world_vertex(this.pannable_viewport.state.pannable_screen_to_virtual_screen(new ScreenPoint(this.width(), this.height())))

        this.renderer.draw_world(ctx, this.world, this.ui, tl, br)
    }

    protected handle_mouse_zoom(x: number, y: number, dz: number): void {
        const sp = new ScreenPoint(x, y)
        this.pannable_viewport.zoom(dz * 0.002, sp)
    }

    public handle_mouse_down(sp: ScreenPoint, btn: PointerButton): void {
        const cmd = this.ui.pending_command
        if (cmd != null) {
            this.commit_command(cmd)
            this.ui.clear_all_selections()
            return
        }

        const tsp = this.pannable_viewport.state.pannable_screen_to_virtual_screen(sp)

        const wp = this.viewport.screen_to_world_vertex(tsp)
        if (this.ui.clicked_coord(wp)) {
            return
        }

        this.dragged_screen_point = sp
    }

    protected handle_mouse_move(x: number, y: number): void {
        const sp = new ScreenPoint(x, y)
        if (this.dragged_screen_point == null) {
            const tsp = this.pannable_viewport.state.pannable_screen_to_virtual_screen(sp)
            this.ui.hovered_point = this.viewport.screen_to_world_vertex(tsp)
        } else {
            const dx = sp.x - this.dragged_screen_point.x
            const dy = sp.y - this.dragged_screen_point.y
            this.pannable_viewport.drag(dx, dy)
        }
    }

    protected handle_mouse_up(): void {
        this.dragged_screen_point = null
        this.pannable_viewport.finish_drag()
    }

    protected handle_mouse_cancel(): void {
        this.handle_mouse_up()
    }
}

